/**
 * PaymentContactModal — P1 (v1-Paritaets-Audit 2026-09-01)
 *
 * v1-Paritaet zu payments_controller#select_contact/#update_contact: reine
 * Contact-Korrektur, AUCH fuer importierte Blockchain-Zahlungen (die volle
 * PaymentFormModal bleibt manuellen Zahlungen vorbehalten — siehe PaymentRow).
 * Nutzt useUpdatePaymentContact (PATCH nur mit contact_id), das das Backend
 * gezielt von der imported_readonly-Sperre ausnimmt.
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useContacts } from '../contacts/useContacts'
import { useContactOption } from '../contacts/useContactOption'
import { toast } from '../../components/Toast/useToast'
import { useUpdatePaymentContact, paymentErrorCode } from './usePaymentMutations'
import type { Payment } from './usePayments'

type Props = {
  payment: Payment
  onClose: () => void
  onSaved?: () => void
}

export function PaymentContactModal({ payment, onClose, onSaved }: Props) {
  const { t } = useTranslation('payments')
  const { contacts } = useContacts({ perPage: 100 })
  const updateContact = useUpdatePaymentContact()

  const [contactId, setContactId] = useState(
    payment.contact_id != null ? String(payment.contact_id) : '',
  )

  // Wie im vollen Formular: ein bereits zugeordneter Kontakt ausserhalb der
  // ersten 100 wird nachgeladen, damit die Auswahl nie leer erscheint.
  const assignedContactId = payment.contact_id
  const contactsLoaded = contacts !== undefined
  const assignedContactMissing =
    contactsLoaded && assignedContactId != null &&
    !(contacts ?? []).some((c) => c.id === assignedContactId)
  const fetchedContact = useContactOption(assignedContactId, assignedContactMissing)

  const contactOptions = useMemo(() => {
    const list = (contacts ?? []).map((c) => ({ id: c.id, name: c.name }))
    if (assignedContactId != null && !list.some((c) => c.id === assignedContactId)) {
      list.unshift(
        fetchedContact ?? { id: assignedContactId, name: t('form.contactUnknown', { id: assignedContactId }) },
      )
    }
    return list
  }, [contacts, assignedContactId, fetchedContact, t])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    try {
      await updateContact.mutateAsync({
        id: payment.id,
        contact_id: contactId ? Number(contactId) : null,
      })
      toast.success(t('contactModal.toastSuccess'))
      onSaved?.()
      onClose()
    } catch (err) {
      toast.error(t(`errors.${paymentErrorCode(err)}`))
    }
  }

  const titleId = 'payment-contact-modal-title'

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid="payment-contact-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="modal">
        <div className="modal-header">
          <h2 id={titleId}>{t('contactModal.title')}</h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label={t('form.closeAriaLabel')}
            onClick={onClose}
            data-testid="payment-contact-modal-close"
          >
            &times;
          </button>
        </div>
        <form onSubmit={(e) => { void handleSubmit(e) }}>
          <div className="modal-body">
            <p className="muted">{t('contactModal.hint')}</p>
            <div className="form-group">
              <label htmlFor="payment-contact-modal-select">{t('form.fields.contact')}</label>
              <select
                id="payment-contact-modal-select"
                className="input"
                value={contactId}
                onChange={(e) => setContactId(e.target.value)}
                data-testid="payment-contact-modal-select"
              >
                <option value="">{t('form.contactNone')}</option>
                {contactOptions.map((c) => (
                  <option key={c.id} value={String(c.id)}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={updateContact.isPending}>
              {t('form.cancelBtn')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={updateContact.isPending}
              data-testid="payment-contact-modal-submit"
            >
              {updateContact.isPending ? t('form.submitPending') : t('contactModal.submitBtn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
